import { alpha, type Theme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@shared/ui';
import { type SyntheticEvent, useMemo, useState } from 'react';
import {
  type EmailPreview,
  type EmailPreviewSender,
  type EmailPreviewState,
  useListEmailPreviewsQuery,
} from './api/emailPreviewsApi';

type Viewport = 'desktop' | 'mobile';

/** Logical width of an iPhone 14/15, the narrower of the two common phone widths. */
const MOBILE_WIDTH = 390;
const RAIL_WIDTH = 250;

/**
 * Gmail's own type and colours, deliberately not Mento's — the point of this chrome is to
 * look like the client the email will be read in. Everything outside the message card uses
 * the app theme instead.
 */
const GMAIL = {
  actionBorder: '#DADCE0',
  actionText: '#3C4043',
  border: '#E8EAED',
  chipBg: '#E8F0FE',
  chipText: '#1967D2',
  font: '"Google Sans", Roboto, Arial, sans-serif',
  muted: '#5F6368',
  text: '#202124',
} as const;

/**
 * extendedColors is typed Partial<Color>, so shades read as possibly undefined. The
 * fallbacks are the same values the theme defines; this just keeps the types honest.
 */
const SAGE = { 100: '#F6F8F6', 400: '#BDCCBE', 500: '#637A6F', 600: '#505A54' } as const;
const sage = (t: Theme, shade: keyof typeof SAGE): string =>
  t.extendedColors.sage[shade] ?? SAGE[shade];

/** Neutral by default, sage when the panel it controls is open. */
const toolbarButtonSx = (active: boolean) => (t: Theme) => ({
  bgcolor: active ? sage(t, 500) : 'transparent',
  borderColor: active ? sage(t, 500) : 'divider',
  color: active ? t.extendedColors.common.white : 'text.primary',
  textTransform: 'none' as const,
  '&:hover': {
    bgcolor: active ? sage(t, 600) : alpha(sage(t, 500), 0.08),
    borderColor: sage(t, 400),
  },
});

/**
 * Renders the email body in a sandboxed iframe, sized to its content.
 *
 * allow-same-origin is required to measure the rendered height — a fully opaque sandbox
 * makes contentDocument unreadable, which pins every email to the default height. Scripts
 * stay blocked because allow-scripts is absent, so the body still cannot execute anything
 * or navigate the admin away, and its CSS cannot escape the frame.
 */
const EmailBody = ({ html, title, width }: { html: string; title: string; width?: number }) => {
  const [height, setHeight] = useState(560);

  // Callers key this component by state id, so a new state remounts it and the height
  // resets rather than briefly showing the previous email's frame size.
  const onLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
    const doc = event.currentTarget.contentDocument;
    if (doc) {
      setHeight(Math.max(240, doc.documentElement.scrollHeight));
    }
  };

  return (
    <Box
      component="iframe"
      onLoad={onLoad}
      sandbox="allow-same-origin"
      srcDoc={html}
      sx={{ border: 0, display: 'block', height, width: width ?? '100%' }}
      title={title}
    />
  );
};

/** Gmail's desktop message view, mocked closely enough to judge width and hierarchy. */
const GmailDesktop = ({
  sender,
  state,
}: {
  sender: EmailPreviewSender;
  state: EmailPreviewState;
}) => (
  <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
    <Paper
      sx={{
        bgcolor: '#fff',
        borderColor: GMAIL.border,
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(60,64,67,.15)',
        fontFamily: GMAIL.font,
        overflow: 'hidden',
      }}
      variant="outlined"
    >
      <Box sx={{ display: 'flex', gap: 1.5, px: 3, pt: 2.5 }}>
        <Typography
          sx={{ color: GMAIL.text, flex: 1, fontFamily: GMAIL.font, fontSize: 22, fontWeight: 400 }}
        >
          {state.subject}
        </Typography>
        <Box
          sx={{
            bgcolor: GMAIL.chipBg,
            borderRadius: 1,
            color: GMAIL.chipText,
            fontFamily: GMAIL.font,
            fontSize: 11,
            height: 'fit-content',
            mt: 0.5,
            px: 0.9,
            py: 0.25,
          }}
        >
          Inbox
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.75, px: 3, pb: 2, pt: 1 }}>
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: '#667C71',
            borderRadius: '50%',
            color: '#fff',
            display: 'flex',
            flexShrink: 0,
            height: 40,
            justifyContent: 'center',
            width: 40,
          }}
        >
          M
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: GMAIL.text, fontFamily: GMAIL.font, fontSize: 14 }}>
            <b>{sender.name}</b> <span style={{ color: GMAIL.muted }}>&lt;{sender.email}&gt;</span>
          </Typography>
          <Typography sx={{ color: GMAIL.muted, fontFamily: GMAIL.font, fontSize: 12 }}>
            to me
          </Typography>
        </Box>
        <Typography
          sx={{ color: GMAIL.muted, fontFamily: GMAIL.font, fontSize: 12, whiteSpace: 'nowrap' }}
        >
          10:24 AM (2 hours ago)
        </Typography>
      </Box>
      <EmailBody html={state.html} key={state.id} title={`${state.name} preview`} />
      <Box sx={{ display: 'flex', gap: 1.25, px: 3, pb: 2.5, pt: 1.75 }}>
        {['Reply', 'Forward'].map((label) => (
          <Box
            key={label}
            sx={{
              border: `1px solid ${GMAIL.actionBorder}`,
              borderRadius: '18px',
              color: GMAIL.actionText,
              fontFamily: GMAIL.font,
              fontSize: 14,
              px: 2.75,
              py: 0.9,
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
    </Paper>
  </Box>
);

/** Gmail's mobile message view at a phone width. */
const GmailMobile = ({
  sender,
  state,
}: {
  sender: EmailPreviewSender;
  state: EmailPreviewState;
}) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '10px solid #1F1F1F',
      borderRadius: '38px',
      boxShadow: 6,
      fontFamily: GMAIL.font,
      mx: 'auto',
      overflow: 'hidden',
      width: MOBILE_WIDTH + 20,
    }}
  >
    <Box
      sx={{
        alignItems: 'center',
        color: GMAIL.text,
        display: 'flex',
        fontFamily: GMAIL.font,
        fontSize: 11,
        fontWeight: 700,
        height: 26,
        justifyContent: 'space-between',
        px: 2.25,
      }}
    >
      <span>9:41</span>
      <span>▮▮▮</span>
    </Box>
    <Typography
      sx={{
        color: GMAIL.text,
        fontFamily: GMAIL.font,
        fontSize: 19,
        lineHeight: 1.3,
        pt: 1.5,
        px: 2,
      }}
    >
      {state.subject}
    </Typography>
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.4, pb: 1.5, pt: 1, px: 2 }}>
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: '#667C71',
          borderRadius: '50%',
          color: '#fff',
          display: 'flex',
          flexShrink: 0,
          fontSize: 14,
          height: 34,
          justifyContent: 'center',
          width: 34,
        }}
      >
        M
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ color: GMAIL.text, fontFamily: GMAIL.font, fontSize: 13, fontWeight: 700 }}
        >
          {sender.name}
        </Typography>
        <Typography sx={{ color: GMAIL.muted, fontFamily: GMAIL.font, fontSize: 11.5 }}>
          to me · 10:24 AM
        </Typography>
      </Box>
    </Box>
    <EmailBody
      html={state.html}
      key={state.id}
      title={`${state.name} mobile preview`}
      width={MOBILE_WIDTH}
    />
  </Box>
);

export default function EmailPreviewPage() {
  const { data, isLoading, error } = useListEmailPreviewsQuery();
  const [emailKey, setEmailKey] = useState<string | null>(null);
  const [stateId, setStateId] = useState<string | null>(null);
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [showTechnical, setShowTechnical] = useState(false);
  const [showLinks, setShowLinks] = useState(false);

  const emails = useMemo(() => data?.emails ?? [], [data]);
  const email: EmailPreview | null = useMemo(
    () => emails.find((e) => e.key === emailKey) ?? emails[0] ?? null,
    [emails, emailKey]
  );
  const state: EmailPreviewState | null = useMemo(
    () => email?.states.find((s) => s.id === stateId) ?? email?.states[0] ?? null,
    [email, stateId]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data || !email || !state) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load email previews.</Alert>
      </Box>
    );
  }

  const hasEmptyLink = state.links.some((link) => link === '');

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <Box
        aria-label="Emails"
        component="nav"
        sx={(t) => ({
          bgcolor: sage(t, 100),
          borderColor: 'divider',
          borderRight: 1,
          borderStyle: 'solid',
          flexShrink: 0,
          overflowY: 'auto',
          width: RAIL_WIDTH,
        })}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1.75 }}>
          <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
            Emails
          </Typography>
        </Box>
        {emails.map((item) => {
          const selected = item.key === email.key;
          return (
            <Box
              aria-current={selected ? 'page' : undefined}
              component="button"
              key={item.key}
              onClick={() => {
                setEmailKey(item.key);
                setStateId(null);
              }}
              sx={(t) => ({
                appearance: 'none',
                bgcolor: selected ? alpha(sage(t, 500), 0.16) : 'transparent',
                border: 0,
                borderLeft: 2,
                borderLeftColor: selected ? sage(t, 500) : 'transparent',
                borderLeftStyle: 'solid',
                cursor: 'pointer',
                display: 'block',
                font: 'inherit',
                px: 2,
                py: 1.25,
                textAlign: 'left',
                width: '100%',
                '&:hover': { bgcolor: alpha(sage(t, 500), 0.09) },
              })}
            >
              <Typography sx={{ fontWeight: selected ? 600 : 400 }} variant="body2">
                {item.name}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {item.recipient}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ pt: 1.5, px: 2.25 }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
            <Typography noWrap sx={{ fontWeight: 600, minWidth: 0 }} variant="subtitle1">
              {state.subject}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                color="inherit"
                onClick={() => setShowTechnical((value) => !value)}
                size="small"
                sx={toolbarButtonSx(showTechnical)}
                variant="outlined"
              >
                Triggers
              </Button>
              <Button
                color="inherit"
                onClick={() => setShowLinks((value) => !value)}
                size="small"
                sx={toolbarButtonSx(showLinks)}
                variant="outlined"
              >
                Links ({state.links.length})
              </Button>
              <ToggleButtonGroup
                aria-label="Preview size"
                exclusive
                onChange={(_, value: Viewport | null) => value && setViewport(value)}
                size="small"
                sx={(t) => ({
                  bgcolor: sage(t, 100),
                  '& .MuiToggleButton-root': {
                    border: 0,
                    color: 'text.secondary',
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                  },
                  '& .Mui-selected': {
                    bgcolor: 'background.paper !important',
                    color: 'text.primary !important',
                    fontWeight: 650,
                  },
                })}
                value={viewport}
              >
                <ToggleButton value="desktop">Desktop</ToggleButton>
                <ToggleButton value="mobile">Mobile</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Tabs
            onChange={(_, value: string) => setStateId(value)}
            sx={(t) => ({
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: 40,
              mt: 1.25,
              '& .MuiTabs-indicator': { backgroundColor: sage(t, 500) },
              '& .MuiTab-root': { color: 'text.secondary', fontSize: 13 },
              '& .Mui-selected': { color: 'text.primary', fontWeight: 650 },
            })}
            value={state.id}
            variant="scrollable"
          >
            {email.states.map((item) => (
              <Tab
                key={item.id}
                label={
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                    {item.name}
                    {item.links.some((link) => link === '') && (
                      // Marks the state from the strip, so a broken link is visible without
                      // selecting each tab in turn.
                      <Box
                        aria-label="contains an empty link"
                        component="span"
                        sx={{
                          bgcolor: 'error.main',
                          borderRadius: '50%',
                          display: 'inline-block',
                          height: 6,
                          width: 6,
                        }}
                      />
                    )}
                  </Box>
                }
                sx={{ minHeight: 40, textTransform: 'none' }}
                value={item.id}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2.25, py: 1.5 }}>
          <Typography variant="body2">
            {email.description}{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              <b>{state.name}:</b> {state.description}
            </Box>
          </Typography>
        </Box>

        {showTechnical && (
          <Box
            sx={(t) => ({
              bgcolor: sage(t, 100),
              borderBottom: 1,
              borderColor: 'divider',
              display: 'grid',
              gap: '0 22px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              px: 2.25,
              py: 1.5,
            })}
          >
            {email.technical.map((fact) => (
              <Box key={fact.label} sx={{ display: 'flex', gap: 1, py: 0.4 }}>
                <Typography
                  color="text.secondary"
                  sx={{ flexShrink: 0, fontWeight: 600, width: 84 }}
                  variant="caption"
                >
                  {fact.label}
                </Typography>
                <Typography sx={{ minWidth: 0, overflowWrap: 'anywhere' }} variant="caption">
                  {fact.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {showLinks && (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              display: 'grid',
              gap: 0.75,
              px: 2.25,
              py: 1.5,
            }}
          >
            {state.links.length === 0 && (
              <Typography color="text.secondary" variant="caption">
                No links in this email.
              </Typography>
            )}
            {state.links.map((link, index) =>
              link === '' ? (
                <Typography
                  color="error"
                  key={`empty-${index}`}
                  sx={{ fontWeight: 600 }}
                  variant="caption"
                >
                  Empty href — the call to action points nowhere.
                </Typography>
              ) : (
                <Typography
                  key={link}
                  sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
                  variant="caption"
                >
                  {link}
                </Typography>
              )
            )}
          </Box>
        )}

        <Box
          sx={(t) => ({
            bgcolor: sage(t, 100),
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: 2.5,
          })}
        >
          {hasEmptyLink && !showLinks && (
            <Alert severity="warning" sx={{ maxWidth: 900, mb: 2, mx: 'auto' }}>
              This state contains an empty link.
            </Alert>
          )}
          {viewport === 'desktop' ? (
            <GmailDesktop sender={data.sender} state={state} />
          ) : (
            <GmailMobile sender={data.sender} state={state} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
